import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Alert, Paper, Button } from '@mui/material';
import { API_CONFIG, getNodeApiHeaders } from '../helpers/constants';

/**
 * OAuth callback for the Google Chat Inbox (DM-only, real-mailbox) provider.
 *
 * Deliberately a SEPARATE route/component from GoogleOAuthCallback.tsx (the Gmail callback),
 * even though both exchange a Google authorization code the same way — see
 * docs/GoogleChatInbox_DM_FE_Implementation.md §5.5/§5.6 for why: AddSpaceModal's existing
 * `handleOAuthData` listener branches on `event.data.type === 'OAUTH_SUCCESS'` and routes it into
 * the EMAIL connect path first. Tagging this flow's postMessage with a distinct
 * `data.provider === 'google_chat_inbox'` (instead of 'gmail') lets that listener branch
 * correctly instead of the two OAuth flows colliding.
 */

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
  scope: string;
  email_id?: string; // the authenticated Google account's email, from the Node exchange endpoint
}

const GoogleChatInboxOAuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleOAuthCallback = async () => {
      try {
        const code = searchParams.get('code');
        const oauthError = searchParams.get('error');

        if (oauthError) {
          if (window.opener) {
            window.opener.postMessage(
              {
                type: 'OAUTH_CANCELLED',
                provider: 'google_chat_inbox',
                error: oauthError,
                errorDescription: searchParams.get('error_description') || undefined,
              },
              window.location.origin
            );
            window.close();
            return;
          }
          setError(`OAuth error: ${oauthError}`);
          setLoading(false);
          return;
        }

        if (!code) {
          setError('No authorization code received');
          setLoading(false);
          return;
        }

        // Must match the redirect_uri used to build the initial auth URL exactly.
        const redirectUri = `${window.location.origin}/auth/google-chat-inbox/callback`;

        // Reuses the same generic Google code-exchange endpoint the Gmail flow uses — it's
        // scope-agnostic (just exchanges code + redirect_uri for tokens against Google's own
        // token endpoint), so no backend change is needed for this to return Chat-scoped tokens.
        const response = await fetch(`${API_CONFIG.NODE_BASE_URL}/rest/google/generateToken`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getNodeApiHeaders(),
          },
          body: JSON.stringify({ code, redirectUri }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const tokenResponse: TokenResponse = await response.json();
        setLoading(false);

        if (window.opener) {
          window.opener.postMessage(
            {
              type: 'OAUTH_SUCCESS',
              data: {
                provider: 'google_chat_inbox',
                ...tokenResponse,
              },
            },
            window.location.origin
          );
          window.close();
        }
      } catch (err) {
        console.error('Error processing Google Chat Inbox OAuth callback:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
        setLoading(false);
      }
    };

    handleOAuthCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleGoBack = () => {
    navigate('/supportSpace');
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Authenticating to Google Chat...
        </Typography>
      </Box>
    );
  }

  // If we have a parent window, don't show the success screen — just close.
  if (window.opener && !error) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        p: 3,
      }}
    >
      <Paper elevation={3} sx={{ p: 4, maxWidth: 600, width: '100%', textAlign: 'center' }}>
        {error ? (
          <>
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
            <Button variant="contained" onClick={handleGoBack}>
              Go Back
            </Button>
          </>
        ) : (
          <>
            <Typography variant="h4" gutterBottom color="success.main">
              ✅ Google Chat Authentication Successful!
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Your Google Chat inbox has been successfully authenticated.
            </Typography>
            <Button variant="contained" onClick={handleGoBack}>
              Continue to Support Spaces
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default GoogleChatInboxOAuthCallback;
